import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Popover,
  Avatar,
  IconButton,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Divider
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import withThemedTooltips from '../../../utils/gridTooltips';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
  Search,
  FilterList,
  Edit,
  Delete,
  Visibility,
  Download,
  PictureAsPdf,
  Person,
  Group,
  TrendingUp,
  Badge as BadgeIcon,
  School as SchoolIcon,
  Wc as WcIcon,
  Cake as CakeIcon,
  LocationOn as LocationIcon,
  Home as HomeIcon,
  Phone as PhoneIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { fetchJson } from '../../../utils/fetchJson';
import { getCurrentAcademicYearId, setCurrentAcademicYearId, onAcademicYearChanged } from '../../../utils/academicYearContext';
import useScopeAutoRefresh from '../../../hooks/useScopeAutoRefresh';
import { usePhase } from '../../../contexts/PhaseContext';

const normalizePhaseCodeValue = (value) => {
  if (value === null || value === undefined) return '';
  const str = value.toString().trim().toLowerCase();
  if (!str) return '';
  if (['nur_pri', 'nursery', 'nursery_primary', 'primary', 'basic'].includes(str)) return 'nur_pri';
  if (['sec', 'secondary', 'second_cycle', 'high_school', 'lycee', 'lycée'].includes(str)) return 'sec';
  return str;
};

const extractPhaseIdFromRecord = (record = {}) => {
  const candidates = [
    record.phaseId,
    record.phaseID,
    record.phase_id,
    record.PhaseId,
    record.PhaseID,
    record.educationPhaseId,
    record.classPhaseId,
    record.Class?.phaseId,
    record.Class?.phaseID,
    record.Class?.phase_id,
    record.Class?.PhaseId,
    record.Class?.PhaseID
  ];
  for (const candidate of candidates) {
    const parsed = Number(candidate);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

const matchesPhaseContext = (primaryRecord, fallbackRecord, resolvedPhaseId, resolvedPhaseCode) => {
  const sources = [primaryRecord, fallbackRecord].filter(Boolean);
  let recordPhaseId = null;
  let recordPhaseCode = '';
  for (const source of sources) {
    if (recordPhaseId === null) {
      const candidateId = extractPhaseIdFromRecord(source);
      if (candidateId !== null) recordPhaseId = candidateId;
    }
    if (!recordPhaseCode) {
      const candidateCode = extractPhaseCodeFromRecord(source);
      if (candidateCode) recordPhaseCode = candidateCode;
    }
  }
  if (resolvedPhaseId && recordPhaseId !== null) {
    return recordPhaseId === resolvedPhaseId;
  }
  if (resolvedPhaseCode && recordPhaseCode) {
    return recordPhaseCode === resolvedPhaseCode;
  }
  return true;
};

const matchesAcademicYearContext = (primaryRecord, fallbackRecord, targetAcademicYearId) => {
  const sources = [primaryRecord, fallbackRecord].filter(Boolean);
  for (const source of sources) {
    const candidate = extractAcademicYearIdFromRecord(source);
    if (candidate !== null && candidate !== undefined) {
      if (targetAcademicYearId) {
        if (String(candidate) === String(targetAcademicYearId)) return true;
      } else {
        return true;
      }
    }
  }
  return !targetAcademicYearId;
};

const formatAcademicYearLabel = (year) => {
  if (!year) return '';
  if (year.name) return year.name;
  if (year.label) return year.label;
  if (year.displayName) return year.displayName;
  if (year.description) return year.description;
  if (year.startYear && year.endYear) return `${year.startYear}/${year.endYear}`;
  return `Year ${year.id || year.academicYearId || year.globalAcademicYearId || ''}`;
};

const resolveAcademicYearOptionId = (year) => {
  if (!year) return '';
  const value =
    year.id ??
    year.academicYearId ??
    year.academic_year_id ??
    year.globalAcademicYearId ??
    year.global_academic_year_id ??
    year.yearId ??
    year.year_id ??
    year.AcademicYearId;
  return value ? String(value) : '';
};

const extractAcademicYearList = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload.years)) return payload.years;
  if (Array.isArray(payload.data?.rows)) return payload.data.rows;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.rows)) return payload.rows;
  if (Array.isArray(payload)) return payload;
  return [];
};

const extractStudentsArray = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload.students)) return payload.students;
  if (Array.isArray(payload.data?.rows)) return payload.data.rows;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.rows)) return payload.rows;
  if (Array.isArray(payload)) return payload;
  return [];
};

const extractClassesArray = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload.classes)) return payload.classes;
  if (Array.isArray(payload.data?.rows)) return payload.data.rows;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.rows)) return payload.rows;
  if (Array.isArray(payload)) return payload;
  return [];
};

const extractClassIdFromRecord = (record = {}) => {
  const candidates = [
    record.classId,
    record.class_id,
    record.ClassId,
    record.Class_id,
    record.Class?.id,
    record.Class?.classId,
    record.Class?.class_id,
    record.id,
    record.ID
  ];
  for (const candidate of candidates) {
    if (candidate !== undefined && candidate !== null && candidate !== '') return candidate;
  }
  return null;
};

const normalizeRecordId = (value) => {
  if (value === null || value === undefined) return '';
  const str = String(value).trim();
  if (!str) return '';
  const num = Number(str);
  if (Number.isFinite(num)) return String(num);
  return str;
};

const normalizeClassesData = (records = []) => {
  if (!Array.isArray(records)) return [];
  return records.map((record) => {
    const normalizedId = normalizeRecordId(record.id);
    if (normalizedId && normalizedId !== record.id) {
      return { ...record, id: normalizedId };
    }
    return record;
  });
};

const normalizeStudentsData = (records = [], availableClasses = []) => {
  if (!Array.isArray(records)) return [];
  const classIndex = new Map();
  availableClasses.forEach((cls) => {
    const id = normalizeRecordId(cls.id);
    if (id) classIndex.set(id, cls);
  });
  return records.map((student) => {
    const rawClassId = extractClassIdFromRecord(student);
    const normalizedClassId = normalizeRecordId(rawClassId);
    const classRecord = student.Class || (normalizedClassId ? classIndex.get(normalizedClassId) : null) || null;
    return {
      ...student,
      classId: normalizedClassId,
      Class: classRecord,
      photoUrl: student.photoUrl || student.photo || ''
    };
  });
};

const extractPhaseCodeFromRecord = (record = {}) => {
  const candidates = [
    record.phaseCode,
    record.phase_code,
    record.PhaseCode,
    record.phase,
    record.Phase,
    record.phaseName,
    record.phase_name,
    record.phaseSlug,
    record.cycleType,
    record.level,
    record.Level,
    record.Class?.phaseCode,
    record.Class?.phase_code,
    record.Class?.PhaseCode,
    record.Class?.phase,
    record.Class?.Phase,
    record.Class?.phaseName,
    record.Class?.phase_name,
    record.Class?.phaseSlug,
    record.Class?.cycleType,
    record.Class?.level,
    record.Class?.Level
  ];
  for (const candidate of candidates) {
    const normalized = normalizePhaseCodeValue(candidate);
    if (normalized) return normalized;
  }
  return '';
};

const extractAcademicYearIdFromRecord = (record = {}) => {
  const candidates = [
    record.academicYearId,
    record.academic_year_id,
    record.AcademicYearId,
    record.academicYear?.id,
    record.academic_year?.id,
    record.yearId,
    record.year_id,
    record.year?.id,
    record.Class?.academicYearId,
    record.Class?.academic_year_id,
    record.Class?.AcademicYearId,
    record.Class?.yearId,
    record.Class?.year_id
  ];
  for (const candidate of candidates) {
    const parsed = Number(candidate);
    if (Number.isFinite(parsed)) return parsed;
    if (typeof candidate === 'string' && candidate.trim()) return candidate;
  }
  return null;
};

const resolveInitialAcademicYearId = () => {
  const current = getCurrentAcademicYearId();
  if (current) return String(current);
  const schoolId =
    localStorage.getItem('schoolId') ||
    localStorage.getItem('activeSchoolId') ||
    localStorage.getItem('scopeSchoolId') ||
    '';
  const ayKey = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
  const legacy = localStorage.getItem(ayKey) || localStorage.getItem('currentAcademicYearId') || '';
  if (!legacy) return '';
  try {
    const trimmed = String(legacy).trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      const parsed = JSON.parse(trimmed);
      const candidate = parsed?.id ?? parsed?.value ?? parsed?.academicYearId ?? parsed?.globalAcademicYearId;
      if (candidate != null && candidate !== '') return String(candidate);
    }
  } catch (_) {}
  return String(legacy);
};

const getStoredPhaseCode = () => {
  try {
    const stored =
      localStorage.getItem('schoolAdmin_activePhase') ||
      localStorage.getItem('activePhase') ||
      '';
    return normalizePhaseCodeValue(stored);
  } catch (error) {
    console.error('Failed to read stored phase code:', error);
    return '';
  }
};

const getStoredPhaseId = () => {
  try {
    const raw =
      localStorage.getItem('schoolAdmin_activePhaseId') ||
      localStorage.getItem('activePhaseId');
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch (error) {
    console.error('Failed to read stored phase id:', error);
    return null;
  }
};

const resolvePhaseIdFromCatalog = (phaseCode) => {
  try {
    const code = normalizePhaseCodeValue(phaseCode);
    if (!code) return null;
    const raw = localStorage.getItem('phaseCatalog');
    if (!raw) return null;
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr) || !arr.length) return null;
    const found = arr.find((p) => normalizePhaseCodeValue(p?.code) === code);
    const id = Number(found?.id);
    return Number.isFinite(id) ? id : null;
  } catch (_) {
    return null;
  }
};

const StudentDirectoryPage = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { activePhase, activePhaseId } = usePhase();
  const [phaseEventCode, setPhaseEventCode] = useState(getStoredPhaseCode);
  const [phaseEventId, setPhaseEventId] = useState(getStoredPhaseId);
  const resolvedPhaseCode = useMemo(() => {
    const fallback = phaseEventCode || getStoredPhaseCode();
    return normalizePhaseCodeValue(activePhase || fallback);
  }, [activePhase, phaseEventCode]);
  const resolvedPhaseId = useMemo(() => {
    const code = normalizePhaseCodeValue(activePhase || phaseEventCode || getStoredPhaseCode());
    const catalogId = resolvePhaseIdFromCatalog(code);
    const raw = activePhaseId || phaseEventId || catalogId || getStoredPhaseId();
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }, [activePhase, activePhaseId, phaseEventCode, phaseEventId]);
  const [selectedAcademicYearId, setSelectedAcademicYearId] = useState(resolveInitialAcademicYearId());
  const [academicYears, setAcademicYears] = useState([]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [debugApi, setDebugApi] = useState({ studentsRes: null, classesRes: null, error: null });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    classId: '',
    status: '', // All by default to include promoted/graduated/transferred/active
    gender: ''
  });
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]); // For bulk delete
  const [studentDialogOpen, setStudentDialogOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [classListPdfLoading, setClassListPdfLoading] = useState(false);
  const [formValues, setFormValues] = useState({
    firstName: '',
    lastName: '',
    studentNumber: '',
    classId: '',
    sex: '',
    dateOfBirth: '',
    status: 'active',
    address: '',
    photo: '',
    guardians: []
  });
  const [photoUploading, setPhotoUploading] = useState(false);
  
  // Hover Bio State
  const [hoveredRow, setHoveredRow] = useState(null);
  const [hoverPosition, setHoverPosition] = useState(null);

  const handleRowMouseEnter = (event) => {
    const id = event.currentTarget.getAttribute('data-id');
    const student = students.find(s => String(s.id) === String(id));
    if (student) {
      setHoveredRow(student);
      setHoverPosition({ mouseX: event.clientX + 12, mouseY: event.clientY + 12 });
    }
  };

  const handleRowMouseMove = (event) => {
    if (!hoveredRow) return;
    setHoverPosition({ mouseX: event.clientX + 12, mouseY: event.clientY + 12 });
  };

  const handleRowMouseLeave = () => {
    setHoveredRow(null);
    setHoverPosition(null);
  };

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const resetPagination = useCallback(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);
  const handlePageChange = useCallback((page) => {
    setPaginationModel((prev) => ({ ...prev, page }));
  }, []);
  const handlePageSizeChange = useCallback((pageSize) => {
    setPaginationModel((prev) => ({ ...prev, page: 0, pageSize }));
  }, []);
  const handleSelectionModelChange = useCallback((newSelection) => {
    if (Array.isArray(newSelection)) {
      setSelectedStudentIds(newSelection);
      return;
    }
    if (newSelection && typeof newSelection === 'object' && Array.isArray(newSelection.selectionModel)) {
      setSelectedStudentIds(newSelection.selectionModel);
      return;
    }
    setSelectedStudentIds([]);
  }, []);

  const loadAcademicYears = useCallback(async () => {
    try {
      const [response, currentResponse] = await Promise.all([
        fetchJson('/api/school-academic-years'),
        fetchJson('/api/school-academic-years/current')
      ]);

      const yearLinks = extractAcademicYearList(response);
      const yearsList = (Array.isArray(yearLinks) ? yearLinks : [])
        .map((entry) => {
          const global = entry?.globalYear || entry?.global_year || entry?.GlobalAcademicYear || null;
          const id = entry?.globalAcademicYearId ?? entry?.globalAcademicYearId ?? global?.id ?? null;
          if (!id) return null;
          return {
            ...(global && typeof global === 'object' ? global : {}),
            id
          };
        })
        .filter(Boolean);

      setAcademicYears(yearsList);

      // Fallback: if the school has not configured SchoolAcademicYear entries yet,
      // use global academic years so the directory can still resolve an academicYearId.
      if (!yearsList.length) {
        try {
          const globals = await fetchJson('/api/global-academic-years');
          const globalList = Array.isArray(globals) ? globals : extractAcademicYearList(globals);
          if (Array.isArray(globalList) && globalList.length) {
            setAcademicYears(globalList);
            if (!selectedAcademicYearId) {
              const currentGlobal = globalList.find((y) => y?.isCurrent) || globalList[0];
              const resolved = currentGlobal?.id ? String(currentGlobal.id) : '';
              if (resolved) {
                setSelectedAcademicYearId(resolved);
                const parsed = Number(resolved);
                if (Number.isFinite(parsed)) {
                  setCurrentAcademicYearId(parsed);
                }
              }
            }
          }
        } catch (fallbackErr) {
          console.error('Failed to load global academic years fallback:', fallbackErr);
        }
        return;
      }

      if (!selectedAcademicYearId) {
        const currentFromResponse =
          currentResponse?.currentGlobalAcademicYearId ||
          currentResponse?.currentYear?.id ||
          '';
        let resolved = currentFromResponse ? String(currentFromResponse) : '';
        if (!resolved && Array.isArray(yearsList) && yearsList.length) {
          resolved = String(yearsList[0]?.id || '');
        }
        if (resolved) {
          setSelectedAcademicYearId(resolved);
          const parsed = Number(resolved);
          if (Number.isFinite(parsed)) {
            setCurrentAcademicYearId(parsed);
          }
        }
      }
    } catch (error) {
      console.error('Failed to load academic years for Student Directory:', error);
    }
  }, [selectedAcademicYearId]);

  useEffect(() => {
    loadAcademicYears();
  }, [loadAcademicYears]);

  useEffect(() => {
    const unsubscribe = onAcademicYearChanged((id) => {
      if (id) {
        setSelectedAcademicYearId(String(id));
      }
    });
    return () => unsubscribe?.();
  }, []);

  useEffect(() => {
    const handlePhaseChange = (event) => {
      const codeFromEvent = normalizePhaseCodeValue(event?.detail?.code || '');
      const resolvedCode = codeFromEvent || getStoredPhaseCode();
      setPhaseEventCode(resolvedCode);
      const idFromCatalog = resolvePhaseIdFromCatalog(resolvedCode);
      setPhaseEventId(idFromCatalog ?? getStoredPhaseId());
      resetPagination();
      setSelectedStudentIds([]);
      setSelectedStudent(null);
      setHoveredRow(null);
      setHoverPosition(null);
      setStudents([]);
      setClasses([]);
    };
    window.addEventListener('app:phase:changed', handlePhaseChange);
    return () => window.removeEventListener('app:phase:changed', handlePhaseChange);
  }, [resetPagination]);

  useEffect(() => {
    if (activePhase) {
      const normalized = normalizePhaseCodeValue(activePhase);
      setPhaseEventCode(normalized);
      const idFromCatalog = resolvePhaseIdFromCatalog(normalized);
      if (idFromCatalog != null) setPhaseEventId(idFromCatalog);
    }
  }, [activePhase]);

  useEffect(() => {
    if (activePhaseId) {
      const parsed = Number(activePhaseId);
      if (Number.isFinite(parsed)) setPhaseEventId(parsed);
    }
  }, [activePhaseId]);

  const handleAcademicYearChange = (value) => {
    setSelectedAcademicYearId(value);
    resetPagination();
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      setCurrentAcademicYearId(parsed);
    }
  };


  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';

      let debugStudentsRes = null, debugClassesRes = null, debugError = null;
      try {
        const headers = {};
        if (schoolId) headers['X-Scope-School-Id'] = String(schoolId);
        if (resolvedPhaseCode) headers['X-Active-Phase'] = resolvedPhaseCode;
        if (resolvedPhaseId) headers['X-Active-Phase-Id'] = String(resolvedPhaseId);
        if (selectedAcademicYearId) headers['X-Academic-Year-Id'] = String(selectedAcademicYearId);

        const classQuery = new URLSearchParams();
        if (selectedAcademicYearId) classQuery.set('academicYearId', String(selectedAcademicYearId));
        if (resolvedPhaseId) classQuery.set('phaseId', String(resolvedPhaseId));
        else if (resolvedPhaseCode) classQuery.set('phaseCode', resolvedPhaseCode);
        const classSuffix = classQuery.toString() ? `?${classQuery.toString()}` : '';

        const studentQuery = new URLSearchParams();
        // Fetch all students by default; apply search/class/status/gender filters client-side.
        if (selectedAcademicYearId) studentQuery.set('academicYearId', String(selectedAcademicYearId));
        if (resolvedPhaseId) studentQuery.set('phaseId', String(resolvedPhaseId));
        else if (resolvedPhaseCode) studentQuery.set('phaseCode', resolvedPhaseCode);
        const studentSuffix = studentQuery.toString() ? `?${studentQuery.toString()}` : '';

        [debugStudentsRes, debugClassesRes] = await Promise.all([
          fetchJson(`/api/students${studentSuffix}`, { headers }),
          fetchJson(`/api/classes${classSuffix}`, { headers })
        ]);
      } catch (fetchError) {
        debugError = fetchError;
        console.error('FETCH ERROR:', fetchError);
        enqueueSnackbar('Fetch error: ' + (fetchError.message || fetchError), { variant: 'error' });
        setStudents(prev => Array.isArray(prev) ? prev : []);
        setClasses(prev => Array.isArray(prev) ? prev : []);
        setDebugApi({ studentsRes: debugStudentsRes, classesRes: debugClassesRes, error: debugError });
        setLoading(false);
        return;
      }
      setDebugApi({ studentsRes: debugStudentsRes, classesRes: debugClassesRes, error: null });
      const rawStudents = extractStudentsArray(debugStudentsRes);
      const rawClasses = extractClassesArray(debugClassesRes);

      const normalizedClasses = normalizeClassesData(rawClasses);
      const filteredClasses = normalizedClasses.filter((cls) => {
        const phaseOk = matchesPhaseContext(cls, null, resolvedPhaseId, resolvedPhaseCode);
        const classHasAcademicYear = extractAcademicYearIdFromRecord(cls) !== null && extractAcademicYearIdFromRecord(cls) !== undefined;
        const ayOk = classHasAcademicYear ? matchesAcademicYearContext(cls, null, selectedAcademicYearId) : true;
        return phaseOk && ayOk;
      });

      let studentsData = normalizeStudentsData(rawStudents, filteredClasses);
      studentsData = studentsData.filter((student) => {
        const phaseOk = matchesPhaseContext(student, student.Class, resolvedPhaseId, resolvedPhaseCode);
        const ayOk = matchesAcademicYearContext(student, student.Class, selectedAcademicYearId);
        return phaseOk && ayOk;
      });

      const classesData = filteredClasses;

      setStudents(studentsData);
      setClasses(classesData);
      setLoading(false);
    } catch (error) {
      console.error('UNEXPECTED ERROR:', error);
      enqueueSnackbar(t('students.errorFetchingData'), { variant: 'error' });
      setLoading(false);
    }
  }, [enqueueSnackbar, resolvedPhaseCode, resolvedPhaseId, selectedAcademicYearId, t]);

  // Auto-refresh after fetchData is defined to avoid ReferenceError
  useScopeAutoRefresh([fetchData]);
  useEffect(() => {
    resetPagination();
    fetchData();
  }, [selectedAcademicYearId, resolvedPhaseId, resolvedPhaseCode, resetPagination, fetchData]);

  useEffect(() => {
    resetPagination();
  }, [filters.classId, filters.status, filters.gender, filters.search, resetPagination]);

  const handleView = (student) => {
    const mapped = mapStudentToForm(student);
    setSelectedStudent(mapped);
    setIsEdit(false);
    setFormValues(mapped);
    setStudentDialogOpen(true);
  };

  const handleEdit = (student) => {
    setSelectedStudent(student);
    setIsEdit(true);
    setFormValues(mapStudentToForm(student));
    setStudentDialogOpen(true);
  };

function mapStudentToForm(student) {
  // Map all guardians
  let guardians = [];
  if (student.Guardians && student.Guardians.length > 0) {
    guardians = student.Guardians.map((g) => {
      const gRaw = g.user?.photo || '';
      const apiBase = resolveApiBaseUrl();
      const gPhoto = gRaw ? (gRaw.startsWith('http') || gRaw.startsWith('data:') ? gRaw : `${apiBase}${gRaw.startsWith('/') ? '' : '/'}${gRaw}`) : '';
      return {
        name: g.user?.name || '',
        phone: g.user?.phone || '',
        email: g.user?.email || '',
        id: g.id,
        userId: g.userId,
        photo: gPhoto
      };
    });
  }
  const apiBase = resolveApiBaseUrl();
  const rawPhotoUrl = student.photoUrl || student.photo || '';
  const resolvedPhoto = rawPhotoUrl
    ? (rawPhotoUrl.startsWith('http') || rawPhotoUrl.startsWith('data:') ? rawPhotoUrl : `${apiBase}${rawPhotoUrl.startsWith('/') ? '' : '/'}${rawPhotoUrl}`)
    : '';
  return {
    ...student,
    photo: resolvedPhoto,
    photoUrl: rawPhotoUrl,
    guardians,
    placeOfBirth: student.placeOfBirth || ''
  };
}

  const handleDelete = (student) => {
    setSelectedStudent(student);
    setDeleteDialogOpen(true);
  };

  // Bulk delete handler
  const handleBulkDelete = () => {
    setBulkDeleteDialogOpen(true);
  };


  const handleDeleteStudent = async () => {
    if (!selectedStudent) return;
    try {
      // Delete guardians first if any
      if (Array.isArray(selectedStudent.Guardians) && selectedStudent.Guardians.length > 0) {
        for (const g of selectedStudent.Guardians) {
          if (g.id) {
            try {
              await fetchJson(`/api/guardians/${g.id}`, { method: 'DELETE' });
            } catch (err) {
              // Optionally notify but continue
              console.warn('Failed to delete guardian', g.id, err);
            }
          }
        }
      }
      await fetchJson(`/api/students/${selectedStudent.id}`, { method: 'DELETE' });
      enqueueSnackbar(t('students.deleteSuccess'), { variant: 'success' });
      setDeleteDialogOpen(false);
      fetchData();
    } catch (error) {
      enqueueSnackbar(t('students.deleteError'), { variant: 'error' });
    }
  };

  // Bulk delete students
  const handleBulkDeleteStudents = async () => {
    if (!selectedStudentIds.length) return;
    try {
      for (const id of selectedStudentIds) {
        const student = students.find(s => String(s.id) === String(id));
        if (!student) continue;
        // Delete guardians first if any
        if (Array.isArray(student.Guardians) && student.Guardians.length > 0) {
          for (const g of student.Guardians) {
            if (g.id) {
              try {
                await fetchJson(`/api/guardians/${g.id}`, { method: 'DELETE' });
              } catch (err) {
                console.warn('Failed to delete guardian', g.id, err);
              }
            }
          }
        }
        await fetchJson(`/api/students/${student.id}`, { method: 'DELETE' });
      }
      enqueueSnackbar(t('students.deleteSuccess'), { variant: 'success' });
      setBulkDeleteDialogOpen(false);
      setSelectedStudentIds([]);
      fetchData();
    } catch (error) {
      enqueueSnackbar(t('students.deleteError'), { variant: 'error' });
    }
  };
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    // Guardian fields (support multiple)
    if (name.startsWith('guardian[')) {
      const match = name.match(/guardian\[(\d+)\]\.(\w+)/);
      if (match) {
        const idx = parseInt(match[1], 10);
        const gField = match[2];
        setFormValues((prev) => {
          const guardians = [...prev.guardians];
          guardians[idx] = { ...guardians[idx], [gField]: value };
          return { ...prev, guardians };
        });
      }
    } else if (name === 'photo') {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!selectedStudent?.id) {
        enqueueSnackbar(t('students.saveStudentBeforePhoto', 'Save the student first before uploading a photo.'), { variant: 'info' });
        return;
      }

      (async () => {
        try {
          setPhotoUploading(true);
          const token = resolveAccessToken();
          const apiBase = resolveApiBaseUrl();

          const headers = {};
          if (token) headers['Authorization'] = `Bearer ${token}`;

          const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
          if (schoolId) {
            headers['X-Scope-School-Id'] = String(schoolId);
            headers['X-School-Id'] = String(schoolId);
          }
          if (resolvedPhaseCode) headers['X-Active-Phase'] = String(resolvedPhaseCode);
          if (resolvedPhaseId) headers['X-Active-Phase-Id'] = String(resolvedPhaseId);
          if (selectedAcademicYearId) headers['X-Academic-Year-Id'] = String(selectedAcademicYearId);

          const form = new FormData();
          form.append('photo', file);

          const uploadUrl = `${apiBase}/api/students/${selectedStudent.id}/photo`;
          const resp = await fetch(uploadUrl, { method: 'POST', headers, body: form, credentials: 'include' });
          const data = await resp.json().catch(() => null);
          if (!resp.ok) {
            const msg = data?.error || data?.message || `Photo upload failed (${resp.status})`;
            throw new Error(msg);
          }

          const relUrl = data?.photoUrl;
          const absolute = relUrl && relUrl.startsWith('http') ? relUrl : (relUrl ? `${apiBase}${relUrl.startsWith('/') ? '' : '/'}${relUrl}` : '');
          setFormValues((prev) => ({ ...prev, photo: absolute || relUrl || '', photoUrl: relUrl || prev.photoUrl || '' }));
          enqueueSnackbar(t('students.photoUploadSuccess', 'Photo uploaded successfully.'), { variant: 'success' });
        } catch (err) {
          console.error('Student photo upload error:', err);
          enqueueSnackbar(err?.message || t('students.photoUploadError', 'Photo upload failed.'), { variant: 'error' });
        } finally {
          setPhotoUploading(false);
        }
      })();
    } else {
      setFormValues((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (photoUploading) {
      enqueueSnackbar(t('students.photoUploading', 'Photo is still uploading. Please wait.'), { variant: 'info' });
      return;
    }
    // Validation
    const requiredFields = ['firstName', 'lastName', 'classId', 'sex', 'dateOfBirth', 'status'];
    for (const field of requiredFields) {
      if (!formValues[field]) {
        enqueueSnackbar(t('students.validationRequired', { field: t(`students.${field}`) }), { variant: 'error' });
        return;
      }
    }
    try {
      const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
      const ctxHeaders = {
        'Content-Type': 'application/json',
        ...(schoolId ? { 'X-Scope-School-Id': String(schoolId), 'X-School-Id': String(schoolId) } : {}),
        ...(resolvedPhaseCode ? { 'X-Active-Phase': String(resolvedPhaseCode) } : {}),
        ...(resolvedPhaseId ? { 'X-Active-Phase-Id': String(resolvedPhaseId) } : {}),
        ...(selectedAcademicYearId ? { 'X-Academic-Year-Id': String(selectedAcademicYearId) } : {})
      };
      // Prepare payload for student update (whitelist fields; exclude deprecated/foreign keys)
      const payload = {
        firstName: formValues.firstName || '',
        lastName: formValues.lastName || '',
        studentNumber: formValues.studentNumber || '',
        classId: formValues.classId || null,
        sex: formValues.sex || '',
        dateOfBirth: formValues.dateOfBirth || '',
        status: formValues.status || 'active',
        address: formValues.address || '',
        placeOfBirth: formValues.placeOfBirth || '',
        photoUrl: formValues.photoUrl || formValues.photo || ''
      };
      if (isEdit && selectedStudent) {
        await fetchJson(`/api/students/${selectedStudent.id}`, {
          method: 'PUT',
          headers: ctxHeaders,
          body: JSON.stringify(payload)
        });
        if (Array.isArray(formValues.guardians)) {
          for (const g of formValues.guardians) {
            if (g.id) {
              await fetchJson(`/api/guardians/${g.id}`, {
                method: 'PUT',
                headers: ctxHeaders,
                body: JSON.stringify({
                  user: {
                    name: g.name,
                    phone: g.phone,
                    email: g.email,
                    photo: g.photo || ''
                  }
                })
              });
            }
          }
        }
        enqueueSnackbar(t('students.updateSuccess'), { variant: 'success' });
      } else {
        await fetchJson('/api/students', {
          method: 'POST',
          headers: ctxHeaders,
          body: JSON.stringify(payload)
        });
        enqueueSnackbar(t('students.createSuccess'), { variant: 'success' });
      }
      setStudentDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Student form submit error:', error);
      // Pass the server's reason through. The most common refusal here is a
      // 403 from enforceCurrentYear — the directory can be VIEWED for a past
      // academic year but not edited — and a generic "could not save" gave the
      // user nothing to act on.
      const reason = error?.userMessage || error?.data?.error || error?.data?.message || '';
      enqueueSnackbar(reason || t('students.saveError'), { variant: 'error' });
    }
    // "Submit attempted." used to fire here unconditionally — outside the
    // try/catch, so it appeared on success AND on failure, immediately after
    // the real message and overwriting it. A save that was refused therefore
    // looked like it had merely been "attempted", with the reason lost.
  };

  const handleExport = async (format) => {
    try {
      const normalizedFormat = String(format || 'csv').toLowerCase() === 'excel' ? 'xlsx' : String(format || 'csv').toLowerCase();
      const headers = {};
      if (filters && filters.classId) headers['X-Class-Id'] = String(filters.classId);
      if (filters && filters.status) headers['X-Student-Status'] = String(filters.status);
      if (filters && filters.gender) headers['X-Student-Gender'] = String(filters.gender);
      if (selectedAcademicYearId) headers['X-Academic-Year-Id'] = String(selectedAcademicYearId);
      if (resolvedPhaseCode) headers['X-Active-Phase'] = resolvedPhaseCode;
      if (resolvedPhaseId) headers['X-Active-Phase-Id'] = String(resolvedPhaseId);
      const params = new URLSearchParams({
        format: normalizedFormat,
        ...filters,
        academicYearId: selectedAcademicYearId || ''
      });
      const requestUrl = buildApiUrl(`/api/students/export?${params.toString()}`);
      const response = await fetch(requestUrl, { headers, credentials: 'include' });
      if (!response.ok) {
        const err = await response.json().catch(() => null);
        const msg = err?.error || err?.message || `Export failed (${response.status})`;
        throw new Error(msg);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const contentDisposition = response.headers.get('content-disposition') || '';
      const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
      a.download = filenameMatch?.[1] || `student-directory-${new Date().toISOString().split('T')[0]}.${normalizedFormat}`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      enqueueSnackbar(t('students.exportSuccess'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t('students.exportError'), { variant: 'error' });
    }
  };

  const resolveAccessToken = () => {
    const tokenKeys = ['impersonationToken', 'accessToken', 'token', 'authToken', 'jwt', 'id_token', 'access_token', 'ACCESS_TOKEN'];
    for (const k of tokenKeys) {
      const v = localStorage.getItem(k) || sessionStorage.getItem(k);
      if (v) return v;
    }
    try {
      const raw = localStorage.getItem('user') || sessionStorage.getItem('user');
      if (raw) {
        const obj = JSON.parse(raw);
        const candidate = obj?.accessToken || obj?.token || obj?.authToken || obj?.jwt || obj?.id_token;
        if (candidate) return candidate;
      }
    } catch (_) {}
    return null;
  };

  const resolveApiBaseUrl = () => {
    const configuredBaseRaw = (
      process.env.REACT_APP_API_BASE_URL ||
      process.env.VITE_API_BASE_URL ||
      process.env.NEXT_PUBLIC_API_BASE_URL ||
      ''
    ).trim();
    const browserHost = typeof window !== 'undefined' ? window.location.hostname : '';
    const isLocalBrowserHost = ['localhost', '127.0.0.1', '0.0.0.0'].includes(browserHost);
    const isLocalNodeEnv = process.env.NODE_ENV === 'development';
    const baseRaw = configuredBaseRaw || ((isLocalBrowserHost || isLocalNodeEnv) ? 'http://localhost:5000' : '');
    return (baseRaw || '').toString().replace(/\/$/, '');
  };

  const buildApiUrl = (path) => {
    const base = resolveApiBaseUrl();
    const isAbsolute = /^https?:\/\//i.test(path);
    if (isAbsolute) return path;

    let basePart = base;
    let pathPart = path.startsWith('/') ? path : `/${path}`;

    const baseEndsWithApi = /\/api$/i.test(basePart);
    const pathStartsWithApi = /^\/api(\b|\/)/i.test(pathPart);
    if (baseEndsWithApi && pathStartsWithApi) {
      pathPart = pathPart.replace(/^\/api/i, '');
    }
    return `${basePart}${pathPart}`;
  };

  const handleDownloadClassListPdf = async () => {
    if (!filters?.classId) {
      enqueueSnackbar(t('students.selectClassFirst', 'Please select a class first.'), { variant: 'warning' });
      return;
    }
    if (!selectedAcademicYearId) {
      enqueueSnackbar(t('students.selectAcademicYearFirst', 'Please select an academic year first.'), { variant: 'warning' });
      return;
    }
    try {
      setClassListPdfLoading(true);
      const token = resolveAccessToken();
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const schoolId = localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || localStorage.getItem('schoolId') || '';
      if (schoolId) {
        headers['X-Scope-School-Id'] = String(schoolId);
        headers['X-School-Id'] = String(schoolId);
      }
      if (resolvedPhaseCode) headers['X-Active-Phase'] = resolvedPhaseCode;
      if (resolvedPhaseId) headers['X-Active-Phase-Id'] = String(resolvedPhaseId);
      headers['X-Academic-Year-Id'] = String(selectedAcademicYearId);
      headers['Accept'] = 'application/pdf';

      const params = new URLSearchParams({
        classId: String(filters.classId),
        academicYearId: String(selectedAcademicYearId)
      });

      const requestUrl = buildApiUrl(`/api/students/class-list-pdf?${params.toString()}`);
      const response = await fetch(requestUrl, { headers, credentials: 'include' });
      if (!response.ok) {
        const err = await response.json().catch(() => null);
        const msg = err?.error || err?.message || `Failed to download PDF (${response.status})`;
        throw new Error(msg);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch = disposition.match(/filename\s*=\s*"?([^";]+)"?/i);
      const cls = classes.find((c) => String(c.id) === String(filters.classId));
      const fallbackName = `class-list-${String(cls?.name || 'class').split(' ').join('_')}-${new Date().toISOString().split('T')[0]}.pdf`;
      const filename = filenameMatch?.[1] || fallbackName;

      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(blobUrl);

      enqueueSnackbar(t('students.pdfDownloadSuccess', 'PDF downloaded successfully.'), { variant: 'success' });
    } catch (error) {
      console.error('Class list PDF download error:', error);
      enqueueSnackbar(error?.message || t('students.pdfDownloadError', 'Failed to download PDF.'), { variant: 'error' });
    } finally {
      setClassListPdfLoading(false);
    }
  };


  // Restore frontend filtering logic for search, class, status, gender
  const filteredStudents = students.filter((s) => {
    // Search by name, student number
    const search = filters.search?.toLowerCase() || '';
    let matchesSearch = true;
    if (search) {
      const guardianNames = Array.isArray(s.Guardians)
        ? s.Guardians.map((g) => g?.user?.name).filter(Boolean).join(' ').toLowerCase()
        : '';
      const className = (s.Class?.name || classes.find(c => c.id === s.classId)?.name || '').toString().toLowerCase();
      matchesSearch = (
        (s.firstName && s.firstName.toLowerCase().includes(search)) ||
        (s.lastName && s.lastName.toLowerCase().includes(search)) ||
        (s.studentNumber && s.studentNumber.toLowerCase().includes(search)) ||
        (guardianNames && guardianNames.includes(search)) ||
        (className && className.includes(search))
      );
    }
    const filterClassId = filters.classId ? String(filters.classId) : '';
    const studentClassId = s.classId !== undefined && s.classId !== null ? String(s.classId) : '';
    let matchesClass = !filterClassId || studentClassId === filterClassId;
    let matchesStatus = !filters.status || s.status === filters.status;
    let matchesGender = !filters.gender || s.sex === filters.gender || s.sex === (filters.gender === 'M' ? 'Male' : filters.gender === 'F' ? 'Female' : filters.gender);
    return matchesSearch && matchesClass && matchesStatus && matchesGender;
  });
  const visibleRowCount = React.useMemo(() => {
    const start = paginationModel.page * paginationModel.pageSize;
    const remaining = filteredStudents.length - start;
    if (remaining <= 0) return 0;
    return Math.min(paginationModel.pageSize, remaining);
  }, [filteredStudents.length, paginationModel.page, paginationModel.pageSize]);
  

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'error';
      case 'graduated': return 'info';
      case 'transferred': return 'warning';
      default: return 'default';
    }
  };

  const getGenderIcon = (gender) => {
    return gender === 'M' ? '👨‍🎓' : '👩‍🎓';
  };

  const columns = [
    {
      field: 'Photo',
      headerName: t('students.photo', 'Photo'),
      width: 60,
      renderCell: (params) => {
        const name = `${params.row.firstName || ''} ${params.row.lastName || ''}`.trim();
        const initials = `${params.row.firstName?.[0] || ''}${params.row.lastName?.[0] || ''}`;
        const rawPhoto = params.row.photoUrl || params.row.photo || '';
        const apiBase = resolveApiBaseUrl();
        const resolvedPhoto = rawPhoto
          ? (rawPhoto.startsWith('http') || rawPhoto.startsWith('data:') ? rawPhoto : `${apiBase}${rawPhoto.startsWith('/') ? '' : '/'}${rawPhoto}`)
          : '';
        const colors = [
          '#f44336', '#e91e63', '#9c27b0', '#3f51b5', '#2196f3', '#009688', '#4caf50', '#ff9800', '#795548', '#607d8b'
        ];
        let colorIdx = 0;
        if (name) {
          colorIdx = name.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % colors.length;
        }
        return (
          <Avatar
            src={resolvedPhoto}
            sx={{ width: 40, height: 40, bgcolor: resolvedPhoto ? undefined : colors[colorIdx], color: '#fff' }}
          >
            {!resolvedPhoto && initials}
          </Avatar>
        );
      }
    },
    {
      field: 'fullName',
      headerName: t('common.name', 'Name'),
      flex: 1,
      width: 180,
      valueGetter: (params) => `${params.row.firstName} ${params.row.lastName}`
    },
    {
      field: 'studentNumber',
      headerName: t('common.studentNumber', 'Student Number'),
      width: 150
    },

    {
      field: 'className',
      headerName: t('common.class', 'Class'),
      width: 100,
      valueGetter: (params) => {
        if (params.row.Class && params.row.Class.name) return params.row.Class.name;
        const found = classes.find(c => c.id === params.row.classId);
        return found ? found.name : 'N/A';
      }
    },
    {
      field: 'guardianName',
      headerName: t('students.guardianName', 'Guardian'),
      width: 200,
      valueGetter: (params) => {
        const guardians = Array.isArray(params.row.Guardians) ? params.row.Guardians : [];
        const names = guardians.map((g) => g?.user?.name).filter(Boolean);
        return names.join(', ');
      }
    },
    {
      field: 'sex',
      headerName: t('common.gender', 'Gender'),
      width: 90,
      valueGetter: (params) => params.row.sex,
      renderCell: (params) => {
        const v = (params.value || '').toString();
        if (v === 'M' || v.toUpperCase() === 'M') return t('students.male', 'Male');
        if (v === 'F' || v.toUpperCase() === 'F') return t('students.female', 'Female');
        return v;
      }
    },
    
    {
      field: 'status',
      headerName: t('common.status', 'Status'),
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.value === 'active' ? 'success' : 'default'}
          size="small"
        />
      )
    },
    // ...actions column removed...
  ];

  // Memoised, because a fresh column array on every render makes the grid
  // rebuild every cell — and this one now creates a component per cell.
  const tooltipColumns = useMemo(() => withThemedTooltips(columns), [columns]);



  // Student Add/Edit Dialog (unchanged, for edit/add only)
  const StudentFormDialog = useCallback(({ open, onClose, isEdit, formValues, onChange, onSubmit, classes }) => (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', fontWeight: 600, fontSize: 22, letterSpacing: 1, mb: 1, py: 2 }}>
        {isEdit
          ? (formValues && formValues.id ? t('students.editStudent', 'Edit Student') : t('students.addStudent', 'Add Student'))
          : t('common.studentDetails', 'Student Details')}
      </DialogTitle>
      <form onSubmit={onSubmit}>
        <DialogContent sx={{ background: '#f7fafd', borderRadius: 2, boxShadow: 1 }}>
          <Grid container spacing={2}>
            {/* Student Photo Upload */}
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1 }}>
                <Avatar src={formValues.photo} sx={{ width: 64, height: 64, mb: 1, bgcolor: !formValues.photo ? '#3f51b5' : undefined, color: '#fff' }}>
                  {!formValues.photo && `${formValues.firstName?.[0] || ''}${formValues.lastName?.[0] || ''}`}
                </Avatar>
                {isEdit && (
                  <Button variant="outlined" component="label" size="small">
                    {t('students.uploadPhoto')}
                    <input type="file" name="photo" accept="image/*" hidden onChange={onChange} />
                  </Button>
                )}
              </Box>
            </Grid>
            {/* Student Info */}
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('students.firstName')}
                name="firstName"
                value={formValues.firstName || ''}
                onChange={onChange}
                fullWidth
                required
                disabled={!isEdit}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('students.lastName')}
                name="lastName"
                value={formValues.lastName || ''}
                onChange={onChange}
                fullWidth
                required
                disabled={!isEdit}
              />
            </Grid>
            {/* Student Number (read-only, not editable) */}
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('students.studentNumber')}
                name="studentNumber"
                value={formValues.studentNumber || ''}
                fullWidth
                InputProps={{ readOnly: true }}
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required disabled={!isEdit}>
                <InputLabel>{t('students.class')}</InputLabel>
                <Select
                  name="classId"
                  value={formValues.classId || ''}
                  onChange={onChange}
                  label={t('students.class')}
                >
                  {classes.map((cls) => (
                    <MenuItem key={cls.id} value={cls.id}> {  cls.name }</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required disabled={!isEdit}>
                <InputLabel>{t('students.gender')}</InputLabel>
                <Select
                  name="sex"
                  value={formValues.sex || ''}
                  onChange={onChange}
                  label={t('students.gender')}
                >
                  <MenuItem value="Male">{t('students.male')}</MenuItem>
                  <MenuItem value="Female">{t('students.female')}</MenuItem>
                  <MenuItem value="M">{t('students.male')}</MenuItem>
                  <MenuItem value="F">{t('students.female')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('students.placeOfBirth', 'Place of Birth')}
                name="placeOfBirth"
                value={formValues.placeOfBirth || ''}
                onChange={onChange}
                fullWidth
                disabled={!isEdit}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('students.address', 'Address')}
                name="address"
                value={formValues.address || ''}
                onChange={onChange}
                fullWidth
                disabled={!isEdit}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('students.dateOfBirth')}
                name="dateOfBirth"
                type="date"
                value={formValues.dateOfBirth ? formValues.dateOfBirth.substring(0, 10) : ''}
                onChange={onChange}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                disabled={!isEdit}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required disabled={!isEdit}>
                <InputLabel>{t('students.status')}</InputLabel>
                <Select
                  name="status"
                  value={formValues.status || 'active'}
                  onChange={onChange}
                  label={t('students.status')}
                >
                  <MenuItem value="active">{t('students.active')}</MenuItem>
                  <MenuItem value="inactive">{t('students.inactive')}</MenuItem>
                  <MenuItem value="graduated">{t('students.graduated')}</MenuItem>
                  <MenuItem value="transferred">{t('students.transferred')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {/* Guardian Info */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 2 }}>{t('students.guardianInfo')}</Typography>
            </Grid>
            {Array.isArray(formValues.guardians) && formValues.guardians.map((g, idx) => (
              <React.Fragment key={g.id || idx}>
                <Grid item xs={12} sm={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Tooltip title={t('students.guardianPhoto')}>
                    <Avatar src={g.photo} sx={{ width: 40, height: 40, bgcolor: !g.photo ? '#009688' : undefined, color: '#fff' }}>
                      {!g.photo && g.name?.[0]}
                    </Avatar>
                  </Tooltip>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label={t('students.guardianName')}
                    name={`guardian[${idx}].name`}
                    value={g.name || ''}
                    onChange={onChange}
                    fullWidth
                    disabled={!isEdit}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label={t('students.guardianPhone')}
                    name={`guardian[${idx}].phone`}
                    value={g.phone || ''}
                    onChange={onChange}
                    fullWidth
                    disabled={!isEdit}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label={t('students.guardianEmail')}
                    name={`guardian[${idx}].email`}
                    value={g.email || ''}
                    onChange={onChange}
                    fullWidth
                    disabled={!isEdit}
                  />
                </Grid>
              </React.Fragment>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1, justifyContent: 'space-between' }}>
          <Button onClick={onClose} color="secondary" variant="outlined">
            {t('common.close', 'Close')}
          </Button>
          {isEdit && (
            <Button type="submit" variant="contained" color="primary" sx={{ minWidth: 120, fontWeight: 600 }}>
              {formValues && formValues.id ? t('common.save', 'Save') : t('common.add', 'Add')}
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  ), [t]);

  // Student ID Card View Dialog (for view only)
  const StudentIdCardDialog = useCallback(({ open, onClose, student, classes }) => {
    if (!student) return null;
    const guardians = Array.isArray(student.guardians) ? student.guardians : [];
    let className = '';
    if (student.Class && student.Class.name) {
      className = student.Class.name;
    } else if (student.classId) {
      const found = classes.find(c => c.id === student.classId || c.id === String(student.classId));
      className = found ? found.name : student.classId;
    }
    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      try { return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }); }
      catch (_) { return String(dateStr).substring(0, 10); }
    };
    const genderDisplay = (sex) => {
      if (sex === 'M' || sex === 'Male') return t('students.male', 'Male');
      if (sex === 'F' || sex === 'Female') return t('students.female', 'Female');
      return sex || '';
    };
    const statusColors = { active: { bg: '#e8f5e9', fg: '#2e7d32' }, inactive: { bg: '#ffebee', fg: '#c62828' }, graduated: { bg: '#e3f2fd', fg: '#1565c0' }, transferred: { bg: '#fff3e0', fg: '#e65100' } };
    const sc = statusColors[student.status] || { bg: '#f5f5f5', fg: '#616161' };
    const infoRows = [
      { icon: <BadgeIcon fontSize="small" />, label: t('students.studentNumber', 'Student No.'), value: student.studentNumber },
      { icon: <SchoolIcon fontSize="small" />, label: t('students.class', 'Class'), value: className },
      { icon: <WcIcon fontSize="small" />, label: t('students.gender', 'Gender'), value: genderDisplay(student.sex) },
      { icon: <CakeIcon fontSize="small" />, label: t('students.dateOfBirth', 'Date of Birth'), value: formatDate(student.dateOfBirth) },
      { icon: <LocationIcon fontSize="small" />, label: t('students.placeOfBirth', 'Place of Birth'), value: student.placeOfBirth },
      { icon: <HomeIcon fontSize="small" />, label: t('students.address', 'Address'), value: student.address },
    ];
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', boxShadow: 16, display: 'flex', flexDirection: 'column', maxHeight: '92vh' } }}
      >
        {/* Header banner with photo */}
        <Box sx={{ flexShrink: 0, background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 55%, #42a5f5 100%)', pt: 4, pb: 3, px: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Avatar
            src={student.photo || student.photoUrl}
            sx={{ width: 104, height: 104, bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: 42, border: '4px solid #fff', boxShadow: 6, mb: 1.5 }}
          >
            {!student.photo && !student.photoUrl && `${student.firstName?.[0] || ''}${student.lastName?.[0] || ''}`}
          </Avatar>
          <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, letterSpacing: 0.5, textAlign: 'center', lineHeight: 1.2 }}>
            {student.firstName} {student.lastName}
          </Typography>
          {student.status && (
            <Chip label={t(`students.${student.status}`, student.status)} size="small"
              sx={{ mt: 1, bgcolor: sc.bg, color: sc.fg, fontWeight: 700, fontSize: 12, height: 24 }}
            />
          )}
        </Box>

        {/* Scrollable body (keeps header + footer/Close always visible) */}
        <Box sx={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
        {/* Info grid */}
        <Box sx={{ px: 3, py: 2.5, bgcolor: '#fff' }}>
          <Grid container spacing={1.5}>
            {infoRows.map((row) =>
              row.value ? (
                <Grid item xs={12} sm={6} key={row.label}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.2, borderRadius: 2, bgcolor: '#f5f7fa', height: '100%' }}>
                    <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center', flexShrink: 0 }}>{row.icon}</Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.2, fontSize: 11 }}>{row.label}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.4 }}>{row.value}</Typography>
                    </Box>
                  </Box>
                </Grid>
              ) : null
            )}
          </Grid>
        </Box>

        {/* Guardian section */}
        <Divider />
        <Box sx={{ px: 3, py: 2, bgcolor: '#f9fbff' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main', mb: guardians.length ? 1.5 : 0.5, display: 'flex', alignItems: 'center', gap: 0.8 }}>
            <Group fontSize="small" /> {t('students.guardianInfo', 'Guardian Information')}
          </Typography>
          {guardians.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {t('students.noGuardians', 'No guardian information available.')}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {guardians.map((g, idx) => (
                <Box key={g.id || idx} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, borderRadius: 2, bgcolor: '#fff', boxShadow: 1 }}>
                  <Avatar src={g.photo} sx={{ width: 52, height: 52, bgcolor: '#00897b', color: '#fff', fontWeight: 700, fontSize: 22, flexShrink: 0 }}>
                    {!g.photo && (g.name?.[0] || '?')}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.3 }}>{g.name || t('common.na', 'N/A')}</Typography>
                    {g.phone && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PhoneIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: 13 }}>{g.phone}</Typography>
                      </Box>
                    )}
                    {g.email && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.2 }}>
                        <EmailIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: 13 }}>{g.email}</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
        </Box>
        {/* End scrollable body */}

        {/* Footer */}
        <Box sx={{ flexShrink: 0, display: 'flex', justifyContent: 'flex-end', px: 3, py: 1.5, bgcolor: '#fff', borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={onClose} variant="outlined" color="secondary" sx={{ minWidth: 110, fontWeight: 600 }}>
            {t('common.close', 'Close')}
          </Button>
        </Box>
      </Dialog>
    );
  }, [t]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, mb: 3 }}>
        <Typography variant="h4">
          {t('students.studentDirectory', 'Student Directory')}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={() => handleExport('xlsx')}
          >
            {t('common.export', 'Export')}
          </Button>
          <Button
            variant="contained"
            startIcon={<PictureAsPdf />}
            disabled={!filters?.classId || classListPdfLoading}
            onClick={handleDownloadClassListPdf}
          >
            {classListPdfLoading
              ? t('students.downloading', 'Downloading...')
              : t('students.downloadClassListPdf', 'Download Class List PDF')}
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label={t('common.search', 'Search')}
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              InputProps={{
                startAdornment: <Search fontSize="small" />
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>{t('students.class', 'Class')}</InputLabel>
              <Select
                value={filters.classId}
                onChange={(e) => setFilters({ ...filters, classId: e.target.value })}
                label={t('students.class', 'Class')}
              >
                <MenuItem value="">{t('common.all', 'All')}</MenuItem>
                {Array.isArray(classes) && classes.length > 0 && classes.map((cls) => (
                  <MenuItem key={cls.id} value={cls.id}>
                    {cls.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>{t('students.status', 'Status')}</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                label={t('students.status', 'Status')}
              >
                <MenuItem value="">{t('common.all', 'All')}</MenuItem>
                <MenuItem value="active">{t('students.active', 'Active')}</MenuItem>
                <MenuItem value="inactive">{t('students.inactive', 'Inactive')}</MenuItem>
                <MenuItem value="graduated">{t('students.graduated', 'Graduated')}</MenuItem>
                <MenuItem value="transferred">{t('students.transferred', 'Transferred')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>{t('students.gender', 'Gender')}</InputLabel>
              <Select
                value={filters.gender}
                onChange={(e) => setFilters({ ...filters, gender: e.target.value })}
                label={t('students.gender', 'Gender')}
              >
                <MenuItem value="">{t('common.all', 'All')}</MenuItem>
                <MenuItem value="M">{t('students.male', 'Male')}</MenuItem>
                <MenuItem value="F">{t('students.female', 'Female')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              startIcon={<FilterList />}
              onClick={() => setFilters({ search: '', classId: '', status: '', gender: '' })}
            >
              {t('common.clear', 'Clear')}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
  {/* Only icon-enhanced summary cards remain below */}

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Group color="primary" />
                <Typography color="textSecondary" gutterBottom>
                  {t('students.totalStudents', 'Total Students')}
                </Typography>
              </Box>
              <Typography variant="h4">
                {filteredStudents.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUp color="success" />
                <Typography color="textSecondary" gutterBottom>
                  {t('students.activeStudents', 'Active Students')}
                </Typography>
              </Box>
              <Typography variant="h4">
                {filteredStudents.filter(s => s.status === 'active').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Person color="info" />
                <Typography color="textSecondary" gutterBottom>
                  {t('students.maleStudents', 'Male Students')}
                </Typography>
              </Box>
              <Typography variant="h4">
                {filteredStudents.filter(s => s.sex === 'Male' || s.sex === 'M').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Person color="secondary" />
                <Typography color="textSecondary" gutterBottom>
                  {t('students.femaleStudents', 'Female Students')}
                </Typography>
              </Box>
              <Typography variant="h4">
                {filteredStudents.filter(s => s.sex === 'Female' || s.sex === 'F').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Data Grid */}
      <Paper sx={{ height: 600 }}>
        {/* THE HOVER TIP IN THE APP'S COLOURS. Left alone, the grid puts a
            native `title` attribute on every cell, and a native title is drawn
            by the operating system — a pale yellow box on Windows, grey on
            macOS — which no theme in this product can reach. See
            utils/gridTooltips for why giving the cell a renderer removes it. */}
        <DataGrid
          rows={filteredStudents}
          columns={tooltipColumns}
          pagination
          page={paginationModel.page}
          pageSize={paginationModel.pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          rowsPerPageOptions={[25, 50, 100]}
          checkboxSelection
          selectionModel={selectedStudentIds}
          onSelectionModelChange={handleSelectionModelChange}
          disableSelectionOnClick
          loading={loading}
          getRowId={(row) => row.id}
          components={{
            Toolbar: () => (
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  {t('students.studentList', 'Student List')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Tooltip title={t('common.view', 'View')}>
                    <span>
                      <IconButton
                        color="info"
                        disabled={selectedStudentIds.length !== 1}
                        onClick={() => {
                          const student = students.find(s => String(s.id) === String(selectedStudentIds[0]));
                          if (student) handleView(student);
                        }}
                        size="large"
                      >
                        <Visibility />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={t('common.edit', 'Edit')}>
                    <span>
                      <IconButton
                        color="primary"
                        disabled={selectedStudentIds.length !== 1}
                        onClick={() => {
                          const student = students.find(s => String(s.id) === String(selectedStudentIds[0]));
                          if (student) handleEdit(student);
                        }}
                        size="large"
                      >
                        <Edit />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={t('common.deleteSelected', 'Delete Selected')}>
                    <span>
                      <IconButton
                        color="error"
                        disabled={selectedStudentIds.length === 0}
                        onClick={handleBulkDelete}
                        size="large"
                      >
                        <Delete />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                    {t('common.showing', 'Showing')} {visibleRowCount} {t('common.of', 'of')} {filteredStudents.length} {t('common.results', 'results')}
                  </Typography>
                </Box>
              </Box>
            )
          }}
          componentsProps={{
            row: {
              onMouseEnter: handleRowMouseEnter,
              onMouseMove: handleRowMouseMove,
              onMouseLeave: handleRowMouseLeave,
            },
          }}
        />
        {/* Hover Bio Popover */}
        <Popover
          sx={{ pointerEvents: 'none' }}
          open={Boolean(hoveredRow && hoverPosition)}
          anchorReference="anchorPosition"
          anchorPosition={
            hoverPosition
              ? { top: hoverPosition.mouseY, left: hoverPosition.mouseX }
              : undefined
          }
          onClose={handleRowMouseLeave}
          disableRestoreFocus
          PaperProps={{
            sx: {
              backgroundColor: 'rgba(255,255,255,0.82)',
              backdropFilter: 'blur(4px)',
              boxShadow: 6
            }
          }}
        >
          {hoveredRow && (
            <Box sx={{ p: 2, display: 'flex', gap: 2, maxWidth: 420, alignItems: 'flex-start' }}>
              <Avatar
                src={(() => { const rp = hoveredRow.photo || hoveredRow.photoUrl || ''; const ab = resolveApiBaseUrl(); return rp ? (rp.startsWith('http') || rp.startsWith('data:') ? rp : `${ab}${rp.startsWith('/') ? '' : '/'}${rp}`) : ''; })()}
                sx={{ width: 60, height: 60, bgcolor: 'primary.main' }}
              >
                {!hoveredRow.photoUrl && !hoveredRow.photo && hoveredRow.firstName?.[0]}
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  {hoveredRow.firstName} {hoveredRow.lastName}
                </Typography>
                <Grid container spacing={0.5} sx={{ mt: 0.5 }}>
                  {[
                    { label: t('students.studentNumber', 'Student Number'), value: hoveredRow.studentNumber },
                    { label: t('students.class', 'Class'), value: hoveredRow.Class?.name || classes.find(c => c.id === hoveredRow.classId)?.name || 'N/A' },
                    { label: t('students.gender', 'Gender'), value: hoveredRow.sex === 'M' ? t('students.male', 'Male') : hoveredRow.sex === 'F' ? t('students.female', 'Female') : hoveredRow.sex },
                    { label: t('students.status', 'Status'), value: hoveredRow.status },
                    { label: t('students.dateOfBirth', 'Date of Birth'), value: hoveredRow.dateOfBirth ? hoveredRow.dateOfBirth.substring(0, 10) : '' },
                    { label: t('students.placeOfBirth', 'Place of Birth'), value: hoveredRow.placeOfBirth || '' },
                    { label: t('students.address', 'Address'), value: hoveredRow.address || '' }
                  ].map((row) => (
                    row.value ? (
                      <Grid item xs={12} key={row.label}>
                        <Typography variant="body2" sx={{ lineHeight: 1.35 }}>
                          <strong>{row.label}:</strong> {row.value}
                        </Typography>
                      </Grid>
                    ) : null
                  ))}
                  {Array.isArray(hoveredRow.Guardians) && hoveredRow.Guardians.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="body2" sx={{ lineHeight: 1.35 }}>
                        <strong>{t('students.guardianInfo', 'Guardian Info')}:</strong>{' '}
                        {hoveredRow.Guardians.map((g) => g?.user?.name).filter(Boolean).join(', ')}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Box>
          )}
        </Popover>
        {/* Empty state */}
        {!loading && students.length === 0 && (
          <Box sx={{ p: 2 }}>
            <Alert severity="info">{t('students.noStudentsFound', 'No students found for the current school/phase.')}</Alert>
          </Box>
        )}
      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onClose={() => setBulkDeleteDialogOpen(false)}>
        <DialogTitle>{t('students.confirmBulkDeleteTitle', 'Delete Selected Students')}</DialogTitle>
        <DialogContent>
          <Typography>{t('students.confirmBulkDelete', 'Are you sure you want to delete the selected students? This will also delete their guardians.')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button color="error" onClick={handleBulkDeleteStudents}>{t('common.delete', 'Delete')}</Button>
        </DialogActions>
      </Dialog>
      </Paper>


      {/* Student Edit Dialog (no add) */}
      <StudentFormDialog
        open={studentDialogOpen && isEdit}
        onClose={() => setStudentDialogOpen(false)}
        isEdit={isEdit}
        formValues={formValues}
        onChange={handleFormChange}
        onSubmit={handleFormSubmit}
        classes={classes}
      />

      {/* Student ID Card View Dialog */}
      <StudentIdCardDialog
        open={studentDialogOpen && !isEdit}
        onClose={() => setStudentDialogOpen(false)}
        student={formValues}
        classes={classes}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('students.confirmDeleteTitle') || 'Delete Student'}</DialogTitle>
        <DialogContent>
          <Typography>{t('students.confirmDelete') || 'Are you sure you want to delete this student?'}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button color="error" onClick={handleDeleteStudent}>{t('common.delete', 'Delete')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
export default StudentDirectoryPage;
